import { createResolver, type FieldDef, type NoteTypeDTO } from '@kartothek/shared';
import { useMemo, useState } from 'react';
import { useNoteIndex, useTypes } from '../hooks';
import { t } from '../i18n';
import { Combobox, useNoteOptions } from './Combobox';
import { ImagePicker } from './ImagePicker';
import { MarkdownBody } from './MarkdownView';

type FM = Record<string, unknown>;

function displayValue(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (Array.isArray(v)) return v.map(displayValue).join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/** Goldwert anzeigen: „1.250 GM“. */
export function formatGold(v: unknown): string {
  const n = typeof v === 'number' ? v : Number(String(v).replace(',', '.'));
  if (!Number.isFinite(n)) return String(v);
  return `${n.toLocaleString('de-DE', { maximumFractionDigits: 2 })} ${t('field.goldUnit')}`;
}

/** Wandelt die Eingabe je nach Feldtyp zurück (Zahl, Ja/Nein, Liste). */
function parseValue(raw: string, type: string | undefined, previous: unknown): unknown {
  if (type === 'number' || type === 'gold') {
    const s = raw.trim().replace(/\s*(gm|gp)$/i, '').replace(/\./g, '').replace(',', '.');
    return s === '' ? null : Number.isNaN(Number(s)) ? raw : Number(s);
  }
  if (type === 'list' || Array.isArray(previous)) {
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => (/^-?\d+(\.\d+)?$/.test(s) && typeof (previous as unknown[] | undefined)?.[0] === 'number' ? Number(s) : s));
  }
  if (typeof previous === 'number' && /^-?\d+(\.\d+)?$/.test(raw.trim())) return Number(raw);
  return raw;
}

/** `[[Ziel|Alias]]` → `Ziel`. */
function linkTarget(v: unknown): string {
  const s = typeof v === 'string' ? v : '';
  const m = /^\s*\[\[([^\]|#]+)(?:#[^\]|]*)?(?:\|[^\]]*)?\]\]\s*$/.exec(s);
  return (m ? m[1]! : s).trim();
}

/** Auswahl einer verknüpften Notiz (z. B. NSC als Anführer). Speichert `[[Titel]]`. */
function LinkPicker(props: {
  cid: string;
  value: string;
  field: FieldDef;
  onCommit: (target: string) => void;
  placeholder?: string;
}) {
  const [text, setText] = useState(props.value);
  const options = useNoteOptions(props.cid, props.field.targetTypes);
  const commit = (v = text) => {
    if (v.trim() !== props.value) props.onCommit(v.trim());
  };
  return (
    <Combobox
      value={text}
      onChange={setText}
      options={options}
      onPick={(o) => {
        setText(o.title);
        commit(o.title);
      }}
      onEnter={() => commit()}
      onBlur={() => commit()}
      placeholder={props.placeholder ?? t('field.pickAny')}
    />
  );
}

/** Mehrfachauswahl (`links`): Chips + Auswahlfeld. */
function LinksPicker(props: {
  cid: string;
  value: unknown;
  field: FieldDef;
  onChange: (v: string[]) => void;
  placeholder?: string;
}) {
  const items = (Array.isArray(props.value) ? props.value : props.value ? [props.value] : []).map(linkTarget).filter(Boolean);
  const [key, setKey] = useState(0);
  return (
    <div className="chips-input">
      {items.map((it, i) => (
        <span key={`${it}-${i}`} className="chip">
          {it}
          <button type="button" className="chip-x" onClick={() => props.onChange(items.filter((_, j) => j !== i).map((x) => `[[${x}]]`))}>
            ×
          </button>
        </span>
      ))}
      <LinkPicker
        key={key}
        cid={props.cid}
        value=""
        field={props.field}
        placeholder={props.placeholder}
        onCommit={(target) => {
          if (!target) return;
          props.onChange([...items, target].map((x) => `[[${x}]]`));
          setKey((k) => k + 1);
        }}
      />
    </div>
  );
}

/** Eigenschaften (Frontmatter) als Formular bzw. als Leseansicht. Statblöcke werden separat dargestellt. */
export function Properties(props: {
  cid: string;
  frontmatter: FM;
  type: NoteTypeDTO | undefined;
  editing: boolean;
  onChange?: (fm: FM) => void;
  links?: Parameters<typeof MarkdownBody>[0]['links'];
  folder?: string;
  isGameMaster: boolean;
  /** In der Leseansicht ausgeblendete Felder (z. B. im Charakterbogen gezeigt). */
  hideKeys?: string[];
}) {
  const { frontmatter, type, editing } = props;
  const [newKey, setNewKey] = useState('');
  const index = useNoteIndex(props.cid);
  const types = useTypes(props.cid);
  const fieldDefs = (type?.fields ?? []).filter((f) => f.type !== 'statblock');
  const hidden = new Set((type?.fields ?? []).filter((f) => f.type === 'statblock').map((f) => f.key));
  hidden.add('statblock');
  hidden.add('sheet');
  if (!editing) for (const k of props.hideKeys ?? []) hidden.add(k);
  if (!editing) for (const f of type?.fields ?? []) if (f.type === 'image') hidden.add(f.key);
  const keys = [
    ...fieldDefs.map((f) => f.key).filter((k) => !hidden.has(k)),
    ...Object.keys(frontmatter).filter((k) => !fieldDefs.some((f) => f.key === k) && !hidden.has(k)),
  ];
  const visibleKeys = editing ? keys : keys.filter((k) => frontmatter[k] !== undefined && frontmatter[k] !== '' && frontmatter[k] !== null);
  // Eindeutigkeit für `[[Titel]]` vs. `[[Ordner/Titel]]`
  const resolver = useMemo(
    () => createResolver((index.data ?? []).map((n) => ({ id: n.id, folder: n.folder, title: n.title }))),
    [index.data],
  );
  if (!editing && visibleKeys.length === 0) return null;

  const set = (key: string, value: unknown) => {
    const next = { ...frontmatter };
    if (value === '' || value === null || (Array.isArray(value) && value.length === 0)) delete next[key];
    else next[key] = value;
    props.onChange?.(next);
  };

  const toLink = (title: string) => {
    if (!title) return '';
    const hit = (index.data ?? []).find((n) => n.title.toLowerCase() === title.toLowerCase());
    if (hit && !resolver.isUniqueNoteTitle(hit.title)) return `[[${hit.folder ? `${hit.folder}/` : ''}${hit.title}]]`;
    return `[[${hit?.title ?? title}]]`;
  };

  const pickHint = (def: FieldDef) =>
    def.targetTypes?.length
      ? t('field.pick', { types: def.targetTypes.map((k) => types.byKey.get(k)?.label ?? k).join(' / ') })
      : t('field.pickAny');

  const renderEdit = (key: string, def: FieldDef | undefined, value: unknown) => {
    if (def?.type === 'boolean' || typeof value === 'boolean') {
      return <input type="checkbox" checked={value === true} onChange={(e) => set(key, e.target.checked)} />;
    }
    if (def?.type === 'image') {
      return <ImagePicker cid={props.cid} value={value} onChange={(v) => set(key, v)} />;
    }
    if (def?.type === 'link') {
      return (
        <LinkPicker
          cid={props.cid}
          value={linkTarget(value)}
          field={def}
          placeholder={pickHint(def)}
          onCommit={(target) => set(key, toLink(target))}
        />
      );
    }
    if (def?.type === 'links') {
      return (
        <LinksPicker
          cid={props.cid}
          value={value}
          field={def}
          placeholder={pickHint(def)}
          onChange={(v) => set(key, v.map((x) => toLink(linkTarget(x))))}
        />
      );
    }
    const listId = def?.options?.length ? `opt-${key}` : undefined;
    return (
      <span className={def?.type === 'gold' ? 'with-unit' : undefined}>
        <input
          defaultValue={displayValue(value)}
          type={def?.type === 'date' ? 'date' : 'text'}
          inputMode={def?.type === 'gold' || def?.type === 'number' ? 'decimal' : undefined}
          list={listId}
          onBlur={(e) => set(key, parseValue(e.target.value, def?.type, value))}
        />
        {def?.type === 'gold' && <span className="unit">{t('field.goldUnit')}</span>}
        {listId && (
          <datalist id={listId}>
            {def!.options!.map((o) => (
              <option key={o} value={o} />
            ))}
          </datalist>
        )}
      </span>
    );
  };

  const renderRead = (def: FieldDef | undefined, value: unknown) => {
    if (def?.type === 'gold') return <span>{formatGold(value)}</span>;
    const v = value === true ? '✓' : value === false ? '✗' : value;
    return (
      <MarkdownBody
        cid={props.cid}
        body={displayValue(v)}
        links={props.links ?? {}}
        folder={props.folder ?? ''}
        depth={2}
        chain={[]}
        isGameMaster={props.isGameMaster}
        preview={false}
      />
    );
  };

  return (
    <div className="properties">
      <table>
        <tbody>
          {visibleKeys.map((key) => {
            const def = fieldDefs.find((f) => f.key === key);
            const value = frontmatter[key];
            return (
              <tr key={key}>
                <th>{def?.label ?? key}</th>
                <td>{editing ? renderEdit(key, def, value) : renderRead(def, value)}</td>
              </tr>
            );
          })}
          {editing && (
            <tr>
              <th>
                <input
                  placeholder={t('note.propertyKey')}
                  value={newKey}
                  onChange={(e) => setNewKey(e.target.value.replace(/\s+/g, '_'))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newKey) {
                      e.preventDefault();
                      set(newKey, '…');
                      setNewKey('');
                    }
                  }}
                />
              </th>
              <td className="muted">{t('note.addProperty')} (Enter)</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
